// ============================================================
// BACKEND
// ============================================================

export const BACKEND =
  "https://quick-production-b60d.up.railway.app";

// ============================================================
// AUTH
// ============================================================

function getAuthToken() {
  /*
   * Your app should store the JWT here after login.
   *
   * Primary key:
   *   mintza_token
   *
   * The fallbacks make this work if your login code
   * currently uses one of these other names.
   */
  const token =
    localStorage.getItem("mintza_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("access_token");

  return token;
}

function authHeaders() {
  const token = getAuthToken();

  console.log(
    "🔐 Auth token:",
    token
      ? `FOUND (${token.length} chars)`
      : "❌ NOT FOUND"
  );

  if (!token) {
    console.warn(
      "⚠️ No authentication token found in localStorage."
    );

    return {};
  }

  return {
    Authorization: `Bearer ${token}`,
  };
}

// ============================================================
// GENERIC API HELPERS
// ============================================================

async function parseResponse(res) {
  const text = await res.text();

  let data = {};

  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = {
      message: text,
    };
  }

  if (!res.ok) {
    const message =
      data?.error ||
      data?.message ||
      text ||
      `Request failed with status ${res.status}`;

    const error = new Error(message);

    error.status = res.status;

    error.response = {
      status: res.status,
      data,
    };

    throw error;
  }

  return data;
}

// ============================================================
// GET
// ============================================================

export async function apiGet(path) {
  const url = `${BACKEND}${path}`;

  const headers = authHeaders();

  console.log("➡️ GET:", url);
  console.log(
    "➡️ Authorization attached:",
    !!headers.Authorization
  );

  const res = await fetch(url, {
    method: "GET",
    headers,
  });

  console.log(
    "⬅️ GET:",
    res.status,
    url
  );

  return parseResponse(res);
}

// ============================================================
// POST
// ============================================================

export async function apiPost(
  path,
  body = {}
) {
  const url = `${BACKEND}${path}`;

  const headers = {
    "Content-Type": "application/json",
    ...authHeaders(),
  };

  console.log("➡️ POST:", url);
  console.log("➡️ Body:", body);
  console.log(
    "➡️ Authorization attached:",
    !!headers.Authorization
  );

  const res = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  console.log(
    "⬅️ POST:",
    res.status,
    url
  );

  return parseResponse(res);
}

// ============================================================
// PUT
// ============================================================

export async function apiPut(
  path,
  body = {}
) {
  const url = `${BACKEND}${path}`;

  const headers = {
    "Content-Type": "application/json",
    ...authHeaders(),
  };

  console.log("➡️ PUT:", url);

  const res = await fetch(url, {
    method: "PUT",
    headers,
    body: JSON.stringify(body),
  });

  console.log(
    "⬅️ PUT:",
    res.status,
    url
  );

  return parseResponse(res);
}

// ============================================================
// PATCH
// ============================================================

export async function apiPatch(
  path,
  body = {}
) {
  const url = `${BACKEND}${path}`;

  const headers = {
    "Content-Type": "application/json",
    ...authHeaders(),
  };

  console.log("➡️ PATCH:", url);

  const res = await fetch(url, {
    method: "PATCH",
    headers,
    body: JSON.stringify(body),
  });

  console.log(
    "⬅️ PATCH:",
    res.status,
    url
  );

  return parseResponse(res);
}

// ============================================================
// DELETE
// ============================================================

export async function apiDelete(path) {
  const url = `${BACKEND}${path}`;

  const headers = authHeaders();

  console.log("➡️ DELETE:", url);

  const res = await fetch(url, {
    method: "DELETE",
    headers,
  });

  console.log(
    "⬅️ DELETE:",
    res.status,
    url
  );

  return parseResponse(res);
}

// ============================================================
// MUSIC
// ============================================================

export async function getTracks() {
  return apiGet("/api/music/tracks");
}

export function getStreamUrl(id) {
  return `${BACKEND}/api/music/stream/${id}`;
}

export async function getMusicFavorites() {
  return apiGet("/api/music/favorites");
}

// ============================================================
// AGORA
// ============================================================

export async function getAgoraToken(channelName) {
  console.log("");
  console.log("========================================");
  console.log("📡 AGORA TOKEN REQUEST");
  console.log("========================================");

  console.log("Backend:", BACKEND);
  console.log("Channel:", channelName);

  if (!channelName) {
    throw new Error(
      "Agora channel name is missing"
    );
  }

  const url =
    `${BACKEND}/api/agora/token`;

  const headers = {
    "Content-Type": "application/json",
    ...authHeaders(),
  };

  const body = {
    channelName: String(channelName),
  };

  let res;

  try {
    res = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });
  } catch (networkError) {
    console.error(
      "❌ FETCH FAILED",
      networkError
    );

    throw new Error(
      `Cannot reach backend: ${networkError.message}`
    );
  }

  console.log(
    "⬅️ Backend HTTP status:",
    res.status
  );

  const text = await res.text();

  console.log(
    "⬅️ Backend response:",
    text
  );

  if (!res.ok) {
    throw new Error(
      `Agora token request failed: ${res.status} ${text}`
    );
  }

  let data;

  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(
      "Backend returned invalid JSON"
    );
  }

  console.log(
    "✅ Agora credentials received:",
    {
      appId: data.appId,
      uid: data.uid,
      channelName: data.channelName,
      expiresIn: data.expiresIn,
      hasToken: !!data.token,
    }
  );

  if (!data.appId) {
    throw new Error(
      "Backend did not return Agora appId"
    );
  }

  if (!data.token) {
    throw new Error(
      "Backend did not return Agora token"
    );
  }

  if (
    data.uid === undefined ||
    data.uid === null
  ) {
    throw new Error(
      "Backend did not return Agora uid"
    );
  }

  return data;
}

// ============================================================
// MEDIA
// ============================================================

export async function uploadMedia(
  formData,
  onProgress
) {
  return fetchWithProgress(
    `${BACKEND}/api/media/upload`,
    {
      method: "POST",
      headers: authHeaders(),
      body: formData,
    },
    onProgress
  );
}

// ============================================================
// MESSAGES
// ============================================================

export async function sendMessage(body) {
  return apiPost(
    "/api/messages/send",
    body
  );
}

// ============================================================
// CHAT
// ============================================================

export async function getChats() {
  return apiGet("/api/chats");
}

export async function getChatRestrictions() {
  return apiGet(
    "/api/me/restrictions"
  );
}

export async function markChatRead(chatId) {
  return apiPost(
    `/api/chats/${chatId}/read`
  );
}

export async function updateChat(
  chatId,
  data
) {
  return apiPatch(
    `/api/chats/${chatId}`,
    data
  );
}

export async function deleteChat(chatId) {
  return apiDelete(
    `/api/chats/${chatId}`
  );
}

export async function createDirectChat(
  userId
) {
  return apiPost(
    "/api/chats/direct",
    {
      userId,
    }
  );
}


/* ============================================================
   SNAPS — CAMERA PHOTO AND VIDEO MESSAGES
   Uses the existing BACKEND, authHeaders and parseResponse.
   ============================================================ */

/**
 * Send a photo or video Snap to a chat.
 *
 * chatId: ID of the chat, not the recipient's user ID.
 * media: Blob, File, or data URL.
 * metadata: { caption, timer, expiresIn }
 */
export async function sendSnap(
  chatId,
  media,
  metadata = {}
) {
  if (!chatId) {
    throw new Error("Chat ID is required.");
  }

  if (!media) {
    throw new Error("No photo or video was captured.");
  }

  let file;

  if (media instanceof Blob) {
    file = media;
  } else if (
    typeof media === "string" &&
    media.startsWith("data:")
  ) {
    const response = await fetch(media);

    if (!response.ok) {
      throw new Error("Could not prepare the captured media.");
    }

    file = await response.blob();
  } else {
    throw new Error("Unsupported Snap media format.");
  }

  const extension = file.type.includes("mp4")
    ? "mp4"
    : file.type.includes("webm")
      ? "webm"
      : file.type.includes("png")
        ? "png"
        : file.type.includes("jpeg")
          ? "jpg"
          : file.type.includes("gif")
            ? "gif"
            : "jpg";

  const formData = new FormData();

  formData.append(
    "media",
    file,
    `snap-${Date.now()}.${extension}`
  );

  formData.append(
    "caption",
    metadata.caption || ""
  );

  formData.append(
    "duration",
    String(metadata.timer ?? 10)
  );

  formData.append(
    "expiresIn",
    String(metadata.expiresIn ?? 86400)
  );

  const response = await fetch(
    `${BACKEND}/api/chats/${encodeURIComponent(chatId)}/snaps`,
    {
      method: "POST",
      headers: authHeaders(),
      body: formData,
    }
  );

  return parseResponse(response);
}

/**
 * Send the same Snap to multiple chats.
 *
 * recipientIds must contain CHAT IDs.
 */
export async function sendSnapToChats(
  media,
  metadata = {}
) {
  const chatIds = [
    ...new Set(
      (
        metadata.recipientIds?.length
          ? metadata.recipientIds
          : [metadata.chatId]
      )
        .filter(Boolean)
        .map(String)
    ),
  ];

  if (!chatIds.length) {
    throw new Error("Select at least one chat.");
  }

  const results = await Promise.all(
    chatIds.map((chatId) =>
      sendSnap(chatId, media, metadata)
    )
  );

  return results;
}

/**
 * Get Snaps for a chat.
 */
export async function getChatSnaps(chatId) {
  if (!chatId) {
    throw new Error("Chat ID is required.");
  }

  return apiGet(
    `/api/chats/${encodeURIComponent(chatId)}/snaps`
  );
}

/**
 * Mark a Snap as opened.
 */
export async function openChatSnap(
  chatId,
  messageId
) {
  if (!chatId || !messageId) {
    throw new Error("Chat ID and Snap ID are required.");
  }

  return apiPost(
    `/api/chats/${encodeURIComponent(chatId)}/snaps/${encodeURIComponent(messageId)}/open`
  );
}

/**
 * Delete a Snap.
 */
export async function deleteChatSnap(
  chatId,
  messageId
) {
  if (!chatId || !messageId) {
    throw new Error("Chat ID and Snap ID are required.");
  }

  return apiDelete(
    `/api/chats/${encodeURIComponent(chatId)}/snaps/${encodeURIComponent(messageId)}`
  );
}


// ============================================================
// USERS
// ============================================================

export async function getSuggestedUsers() {
  return apiGet(
    "/api/users/suggested"
  );
}

export async function blockUser(
  userId
) {
  return apiPost(
    "/api/users/block",
    {
      userId,
    }
  );
}

export async function reportUser(
  userId,
  reason = "From chat menu"
) {
  return apiPost(
    "/api/users/report",
    {
      userId,
      reason,
    }
  );
}

// ============================================================
// MESSAGE REQUESTS
// ============================================================

/*
 * IMPORTANT:
 *
 * Backend route:
 *
 * GET /api/chats/requests
 *
 * NOT:
 *
 * GET /api/message-requests
 *
 * The backend explicitly defines /api/chats/requests
 * and protects it with authenticateToken.
 */

export async function getMessageRequests() {
  return apiGet(
    "/api/chats/requests"
  );
}

/*
 * Backend route:
 *
 * POST /api/chats/requests/:chatId/accept
 */

export async function acceptChatRequest(
  chatId
) {
  if (!chatId) {
    throw new Error(
      "Chat ID is required"
    );
  }

  return apiPost(
    `/api/chats/requests/${chatId}/accept`
  );
}

/*
 * Backend route:
 *
 * POST /api/chats/requests/:chatId/decline
 */

export async function declineChatRequest(
  chatId
) {
  if (!chatId) {
    throw new Error(
      "Chat ID is required"
    );
  }

  return apiPost(
    `/api/chats/requests/${chatId}/decline`
  );
}

// ============================================================
// PRODUCTS
// ============================================================

export async function getProducts(
  params = {}
) {
  const queryString =
    new URLSearchParams(
      params
    ).toString();

  const path =
    `/api/products` +
    (
      queryString
        ? `?${queryString}`
        : ""
    );

  return apiGet(path);
}

export async function createProduct(
  formData,
  onProgress
) {
  return fetchWithProgress(
    `${BACKEND}/api/products`,
    {
      method: "POST",
      headers: authHeaders(),
      body: formData,
    },
    onProgress
  );
}

// ============================================================
// PROFILE
// ============================================================

export async function getProfile() {
  return apiGet(
    "/api/users/me"
  );
}

export async function updateProfile(
  data
) {
  return apiPut(
    "/api/users/me",
    data
  );
}

export async function uploadProfilePic(
  file
) {
  const formData =
    new FormData();

  formData.append(
    "avatar",
    file
  );

  const res = await fetch(
    `${BACKEND}/api/users/me/avatar`,
    {
      method: "POST",
      headers: authHeaders(),
      body: formData,
    }
  );

  return parseResponse(res);
}

export async function uploadCoverPhoto(
  file
) {
  const formData =
    new FormData();

  formData.append(
    "cover",
    file
  );

  const res = await fetch(
    `${BACKEND}/api/users/me/cover`,
    {
      method: "POST",
      headers: authHeaders(),
      body: formData,
    }
  );

  return parseResponse(res);
}

export async function getMyVideos() {
  return apiGet(
    "/api/users/me/videos"
  );
}

export async function getMyShorts() {
  return apiGet(
    "/api/users/me/shorts"
  );
}

// ============================================================
// ORDERS
// ============================================================

export async function checkout(
  cartItems
) {
  return apiPost(
    "/api/orders/checkout",
    {
      items: cartItems,
    }
  );
}

export async function getSellerOrders(
  status = "all"
) {
  return apiGet(
    `/api/seller/orders?status=${encodeURIComponent(
      status
    )}`
  );
}

export async function updateOrderStatus(
  id,
  data
) {
  return apiPut(
    `/api/seller/orders/${id}`,
    data
  );
}

// ============================================================
// EXPLORE
// ============================================================

export async function getExploreVideos() {
  return apiGet(
    "/api/videos/explore?limit=20"
  );
}

export async function getExploreShorts() {
  return apiGet(
    "/api/shorts/explore?limit=20"
  );
}

export async function getExploreUsers() {
  return apiGet(
    "/api/users/explore?limit=20"
  );
}

// ============================================================
// GIPHY
// ============================================================

export async function fetchGiphyGifs(
  searchTerm,
  limit = 20
) {
  const apiKey =
    process.env.REACT_APP_GIPHY_KEY ||
    "kMDqX78LQwjvOg4dzdFS1V3Sij8oSzkJ";

  if (!apiKey) {
    console.warn(
      "REACT_APP_GIPHY_KEY is not configured"
    );

    return {
      data: [],
    };
  }

  const url = searchTerm
    ? `https://api.giphy.com/v1/gifs/search?api_key=${apiKey}&q=${encodeURIComponent(
        searchTerm
      )}&limit=${limit}&rating=pg-13&lang=en`
    : `https://api.giphy.com/v1/gifs/trending?api_key=${apiKey}&limit=${limit}&rating=pg-13`;

  const res = await fetch(url);

  if (!res.ok) {
    throw new Error(
      `Failed to fetch GIPHY GIFs: ${res.status}`
    );
  }

  return res.json();
}

// ============================================================
// INTERNAL UPLOAD HELPER
// ============================================================

async function fetchWithProgress(
  url,
  opts = {},
  onProgress
) {
  if (!onProgress) {
    const res = await fetch(
      url,
      opts
    );

    return parseResponse(res);
  }

  return new Promise(
    (resolve, reject) => {
      const xhr =
        new XMLHttpRequest();

      xhr.open(
        opts.method || "GET",
        url
      );

      const headers =
        opts.headers || {};

      Object.entries(
        headers
      ).forEach(
        ([key, value]) => {
          if (
            !(
              opts.body instanceof
              FormData
            ) ||
            key.toLowerCase() !==
              "content-type"
          ) {
            xhr.setRequestHeader(
              key,
              value
            );
          }
        }
      );

      xhr.upload.onprogress =
        (event) => {
          if (
            event.lengthComputable
          ) {
            onProgress(
              Math.round(
                (event.loaded /
                  event.total) *
                  100
              ),
              event.loaded,
              event.total
            );
          }
        };

      xhr.onload = () => {
        let data = {};

        try {
          data = JSON.parse(
            xhr.responseText ||
              "{}"
          );
        } catch {
          data = {
            message:
              xhr.responseText,
          };
        }

        if (
          xhr.status >= 200 &&
          xhr.status < 300
        ) {
          resolve(data);
          return;
        }

        const error =
          new Error(
            data?.error ||
              data?.message ||
              `Request failed: ${xhr.status}`
          );

        error.status =
          xhr.status;

        error.response = {
          status: xhr.status,
          data,
        };

        reject(error);
      };

      xhr.onerror = () => {
        reject(
          new Error(
            "Upload failed"
          )
        );
      };

      xhr.send(opts.body);
    }
  );
}
